/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

// Enhanced Teacher Dashboard - Shows comprehensive stats for teacher's students
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { 
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Cell, ReferenceLine
} from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Skeleton } from "../ui/skeleton";
import { Alert, AlertDescription } from "../ui/alert";
import { useToast } from "../ui/use-toast";

// Color constants for consistent chart styling
const COLORS = {
  primary: '#2563eb',    // blue-600
  secondary: '#f97316',  // orange-500
  success: '#22c55e',    // green-500
  danger: '#ef4444',     // red-500
  warning: '#f59e0b',    // amber-500
  info: '#0ea5e9',       // sky-500
  accent: '#8b5cf6',     // violet-500
  chartBars: ['#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe'],
  pieColors: ['#2563eb', '#f97316', '#22c55e', '#ef4444', '#f59e0b', '#0ea5e9']
};

const TeacherDashboard = ({ user }) => {
  // State management
  const [stats, setStats] = useState({
    studentStats: { total: 0, activeToday: 0, needingHelp: 0 },
    testStats: { assigned: 0, completed: 0, pending: 0 },
    performanceStats: { averageScore: 0, passRate: 0 }
  });
  const [testPerformance, setTestPerformance] = useState([]);
  const [studentActivity, setStudentActivity] = useState([]);
  const [scoreDistribution, setScoreDistribution] = useState([]);
  const [recentTestResults, setRecentTestResults] = useState([]);
  const [allRecentResults, setAllRecentResults] = useState([]);
  const [displayCount, setDisplayCount] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const { toast } = useToast();

  // Fetch all data needed for the teacher dashboard
  const fetchDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Fetch student progress data for teacher's cohorts
      const progressResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/teacher/students-progress`, {
        credentials: 'include'
      });
      if (!progressResponse.ok) throw new Error('Failed to fetch student progress');
      const progressData = await progressResponse.json();
      
      // Fetch cohorts to get teacher's assigned cohorts
      const cohortsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/teacher/cohorts`, {
        credentials: 'include'
      });
      if (!cohortsResponse.ok) throw new Error('Failed to fetch cohorts');
      const cohortsData = await cohortsResponse.json();
      
      // Process and transform data for dashboard components
      processTeacherData(progressData, cohortsData);
      
    } catch (error) {
      console.error('Error fetching teacher dashboard data:', error);
      setError('Failed to load dashboard data');
      toast({
        title: 'Error',
        description: 'Failed to load dashboard statistics',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Fetch real student activity data
  const fetchStudentActivity = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/teacher/student-activity`, {
        credentials: 'include'
      });

      if (response.ok) {
        const activityData = await response.json();
        // Transform the API response to match chart format
        const transformedData = activityData.map(day => ({
          date: day.date,
          logins: day.logins,
          testsTaken: day.tests_taken
        }));
        setStudentActivity(transformedData);
      } else {
        console.error('Failed to fetch student activity:', response.status);
        // Fallback to empty data
        setStudentActivity([]);
      }
    } catch (error) {
      console.error('Error fetching student activity:', error);
      setStudentActivity([]);
    }
  }, []);

  // Process and transform the fetched data for teacher-specific visualization
  const processTeacherData = (progressData, cohortsData) => {
    // 1. Calculate student statistics
    const totalStudents = progressData.length;
    const studentsWithRecentActivity = progressData.filter(student => {
      const lastLogin = student.last_login ? new Date(student.last_login) : null;
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      return lastLogin && lastLogin > oneDayAgo;
    }).length;
    
    // Students who failed their last test or have low scores
    const studentsNeedingHelp = progressData.filter(student => {
      if (!student.test_results || student.test_results.length === 0) return true;
      const latestResult = student.test_results[0]; // Assuming sorted by latest
      return latestResult.raw_percentage < 70;
    }).length;

    const studentStats = {
      total: totalStudents,
      activeToday: studentsWithRecentActivity,
      needingHelp: studentsNeedingHelp
    };

    // 2. Calculate test statistics
    const allTestResults = [];
    const testMap = new Map();
    let completedTests = 0;
    
    progressData.forEach(student => {
      student.test_results.forEach(result => {
        allTestResults.push(result);
        
        if (!testMap.has(result.test_id)) {
          testMap.set(result.test_id, {
            id: result.test_id,
            name: result.test_name,
            scores: [],
            results: [], // Store full result objects for passed status
            attempts: 0,
            timeTaken: []
          });
        }
        
        const testData = testMap.get(result.test_id);
        testData.scores.push(parseFloat(result.raw_percentage));
        testData.results.push(result); // Store full result for passed status
        testData.attempts += 1;
        if (result.time_spent) {
          testData.timeTaken.push(parseInt(result.time_spent));
        }

        // Use database passed status instead of hardcoded threshold
        if (result.passed === true) {
          completedTests++;
        }
      });
    });

    const testStats = {
      assigned: testMap.size,
      completed: Math.round(completedTests / Math.max(allTestResults.length, 1) * 100),
      pending: allTestResults.filter(result => result.raw_percentage < 90).length
    };

    // 3. Calculate test performance data
    const testPerformanceData = [];
    testMap.forEach(test => {
      const totalScores = test.scores.reduce((sum, score) => sum + score, 0);
      const avgScore = test.scores.length > 0 ? totalScores / test.scores.length : 0;
      // Use database passed status for pass rate calculation
      const passRate = test.results ? (test.results.filter(result => result.passed === true).length / test.results.length * 100) : 0;
      const avgTime = test.timeTaken.length > 0 ? 
        test.timeTaken.reduce((sum, time) => sum + time, 0) / test.timeTaken.length : 0;
      
      testPerformanceData.push({
        id: test.id,
        name: test.name,
        averageScore: avgScore.toFixed(1),
        passRate: passRate.toFixed(1),
        attempts: test.attempts,
        avgCompletionTime: (avgTime / 60).toFixed(1) // Convert to minutes
      });
    });

    // 4. Create score distribution data
    const allScores = allTestResults.map(result => parseFloat(result.raw_percentage));
    const scoreDistributionData = [
      { range: '0-9', count: 0 },
      { range: '10-19', count: 0 },
      { range: '20-29', count: 0 },
      { range: '30-39', count: 0 },
      { range: '40-49', count: 0 },
      { range: '50-59', count: 0 },
      { range: '60-69', count: 0 },
      { range: '70-79', count: 0 },
      { range: '80-89', count: 0 },
      { range: '90-100', count: 0 }
    ];
    
    allScores.forEach(score => {
      if (score < 10) scoreDistributionData[0].count++;
      else if (score < 20) scoreDistributionData[1].count++;
      else if (score < 30) scoreDistributionData[2].count++;
      else if (score < 40) scoreDistributionData[3].count++;
      else if (score < 50) scoreDistributionData[4].count++;
      else if (score < 60) scoreDistributionData[5].count++;
      else if (score < 70) scoreDistributionData[6].count++;
      else if (score < 80) scoreDistributionData[7].count++;
      else if (score < 90) scoreDistributionData[8].count++;
      else scoreDistributionData[9].count++;
    });

    // Note: Student activity data is now fetched separately via fetchStudentActivity()

    // 6. Calculate overall performance statistics
    const allTestScores = allScores.length > 0 ? 
      allScores.reduce((sum, score) => sum + score, 0) / allScores.length : 0;
    // Calculate overall pass rate using database passed status
    const overallPassRate = allTestResults.length > 0 ?
      (allTestResults.filter(result => result.passed === true).length / allTestResults.length * 100) : 0;

    // 7. Get recent test results (deduplicated by result_id)
    const uniqueResults = new Map();
    allTestResults.forEach(result => {
      const existing = uniqueResults.get(result.result_id);
      if (!existing || new Date(result.attempt_timestamp) > new Date(existing.attempt_timestamp)) {
        uniqueResults.set(result.result_id, result);
      }
    });

    const allRecentResultsData = Array.from(uniqueResults.values())
      .sort((a, b) => new Date(b.attempt_timestamp) - new Date(a.attempt_timestamp))
      .map(result => {
        const student = progressData.find(s => s.test_results.some(tr => tr.result_id === result.result_id));
        return {
          user_name: student ? `${student.first_name} ${student.last_name}` : 'Unknown',
          test_name: result.test_name,
          score: result.raw_percentage,
          passed: result.passed
        };
      });

    const recentResults = allRecentResultsData.slice(0, displayCount);

    // Set all the processed data to state
    setStats({
      studentStats,
      testStats,
      performanceStats: { 
        averageScore: allTestScores, 
        passRate: overallPassRate 
      }
    });
    setTestPerformance(testPerformanceData);
    setScoreDistribution(scoreDistributionData);
    setAllRecentResults(allRecentResultsData);
    setRecentTestResults(recentResults);
  };

  // Update displayed results when display count changes
  useEffect(() => {
    if (allRecentResults.length > 0) {
      setRecentTestResults(allRecentResults.slice(0, displayCount));
    }
  }, [displayCount, allRecentResults]);

  // Fetch data on component mount
  useEffect(() => {
    fetchDashboardData();
    fetchStudentActivity();
    // Set up refresh interval (every 5 minutes)
    const intervalId = setInterval(() => {
      fetchDashboardData();
      fetchStudentActivity();
    }, 300000);

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [fetchDashboardData, fetchStudentActivity]);

  // Handle loading more results
  const handleLoadMore = () => {
    setDisplayCount(prev => prev + 10);
  };

  // Rendering loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-5 w-1/2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-10 w-1/3" />
                <Skeleton className="h-4 w-3/4 mt-2" />
              </CardContent>
            </Card>
          ))}
        </div>
        
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-1/4" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-[300px] w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }


  // Main teacher dashboard render
  return (
    <div className="space-y-6">
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Summary Cards */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">My Students</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.studentStats.total}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {stats.studentStats.activeToday} active today • {stats.studentStats.needingHelp} need help
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Test Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.testStats.assigned}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {stats.testStats.completed}% pass rate • {stats.testStats.pending} pending
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Class Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.performanceStats.averageScore.toFixed(1)}%</div>
            <div className="text-xs text-muted-foreground mt-1">
              {stats.performanceStats.passRate.toFixed(1)}% overall pass rate
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Analysis Tabs */}
      <Tabs defaultValue="performance" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="performance">Class Performance</TabsTrigger>
          <TabsTrigger value="activity">Student Activity</TabsTrigger>
          <TabsTrigger value="tests">Test Insights</TabsTrigger>
        </TabsList>
        
        <TabsContent value="performance">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Score Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Score Distribution</CardTitle>
                <CardDescription>Distribution of test scores across your students</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={scoreDistribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="range" />
                      <YAxis />
                      <Tooltip />
                      <ReferenceLine x="90-100" stroke={COLORS.success} label="Pass Threshold" />
                      <Bar dataKey="count" name="Students">
                        {scoreDistribution.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.range === '90-100' ? COLORS.success : COLORS.primary} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            {/* Test Performance Comparison */}
            <Card>
              <CardHeader>
                <CardTitle>Test Performance</CardTitle>
                <CardDescription>Average scores and pass rates by test</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={testPerformance.slice(0, 5)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 100]} />
                      <YAxis type="category" dataKey="name" width={120} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="averageScore" name="Avg. Score %" fill={COLORS.primary} />
                      <Bar dataKey="passRate" name="Pass Rate %" fill={COLORS.success} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="activity">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Student Activity Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Student Activity (7-Day Trend)</CardTitle>
                <CardDescription>Daily logins and tests taken by your students</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={studentActivity}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="logins" stroke={COLORS.primary} name="Logins" />
                      <Line type="monotone" dataKey="testsTaken" stroke={COLORS.secondary} name="Tests Taken" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            {/* Students Needing Attention */}
            <Card>
              <CardHeader>
                <CardTitle>Students Needing Attention</CardTitle>
                <CardDescription>Students with low scores or no recent activity</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-red-500">{stats.studentStats.needingHelp}</div>
                    <div className="text-sm text-muted-foreground mt-2">
                      Students scoring below 70% or with no recent activity
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Check the Students tab for detailed information
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="tests">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Most Attempted Tests */}
            <Card>
              <CardHeader>
                <CardTitle>Most Attempted Tests</CardTitle>
                <CardDescription>Tests with the highest number of attempts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={testPerformance.sort((a, b) => b.attempts - a.attempts).slice(0, 5)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="attempts" name="Attempts" fill={COLORS.info}>
                        {testPerformance.slice(0, 5).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS.chartBars[index % COLORS.chartBars.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            {/* Test Completion Times */}
            <Card>
              <CardHeader>
                <CardTitle>Average Completion Times</CardTitle>
                <CardDescription>Average time to complete each test (minutes)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={testPerformance.sort((a, b) => b.avgCompletionTime - a.avgCompletionTime).slice(0, 5)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="avgCompletionTime" name="Avg. Time (min)" fill={COLORS.accent}>
                        {testPerformance.slice(0, 5).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS.chartBars[index % COLORS.chartBars.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Recent Activities */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Test Results</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            {recentTestResults.map((result, index) => (
              <div key={index} className="flex justify-between items-center py-2 border-b">
                <div className="font-medium">{result.user_name}</div>
                <div className="flex items-center">
                  <span className="text-sm text-muted-foreground mr-2">{result.test_name}:</span>
                  <span className={`font-medium ${result.passed === true ? 'text-green-600' : 'text-red-600'}`}>
                    {parseFloat(result.score).toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
            {recentTestResults.length === 0 && (
              <div className="text-center py-4 text-muted-foreground">No recent test results</div>
            )}
            {recentTestResults.length > 0 && displayCount < allRecentResults.length && (
              <div className="pt-4 text-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLoadMore}
                  className="text-sm"
                >
                  Load More ({allRecentResults.length - displayCount} more)
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeacherDashboard;